import { useSesion } from '@/modules/auth/useSesion'
import { useEffect, useState, type FormEvent } from 'react'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import {
  BuscadorCliente,
  type ClienteElegido,
} from '@/modules/comercial/BuscadorCliente'
import { useOpcionesServicios } from '@/shared/datos/useOpciones'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { AreaTexto } from '@/shared/ui/Formulario/AreaTexto'
import { Campo } from '@/shared/ui/Formulario/Campo'
import { Selector } from '@/shared/ui/Formulario/Selector'
import { Modal } from '@/shared/ui/Modal/Modal'

import { NuevoServicio } from './NuevoServicio'
import { SelectorUbicacion } from './SelectorUbicacion'
import { useUbicarUna, useGuardarUbicacionManual } from '@/modules/mapa-calor/useUbicarOrdenes'

import { useAccionesOrdenServicio } from './useOrdenesServicio'
import estilos from './FormularioNuevaOrden.module.css'

type PropsFormularioNuevaOrden = {
  /**
   * Se avisa con la orden recién creada.
   *
   * Lo usa la pantalla del técnico para encadenar: levanta el trabajo y sigue
   * derecho a cotizarlo, sin volver a buscarlo en ninguna lista. Quien no lo
   * pasa se queda con la confirmación de siempre.
   */
  onCreada?: (orden: { id: string; numero: string; cliente: string; direccion: string }) => void
  abierto: boolean
  onCerrar: () => void
}

/** Fecha de hoy en Bogotá, en el formato que espera un campo de tipo date. */
function hoyEnBogota(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

const VALORES_INICIALES = {
  servicio_id: '',
  direccion: '',
  descripcion: '',
  notas: '',
}

/**
 * Creación de la orden de servicio.
 *
 * Estación 1 del recorrido. Pide lo mínimo —cliente, servicio y dirección— y
 * nada de dinero: la orden nace antes de que exista precio, y desde ese
 * momento ya está contada en los tableros.
 */
export function FormularioNuevaOrden({ abierto, onCerrar , onCreada}: PropsFormularioNuevaOrden) {
  const { crear } = useAccionesOrdenServicio()
  const { perfil } = useSesion()
  const puedeCrearServicio = perfil?.rol === 'gerencia' || perfil?.rol === 'administrador'
  const ubicarUna = useUbicarUna()
  const { guardar: guardarUbicacion } = useGuardarUbicacionManual()
  // Qué pasó con la dirección, para decirlo en la pantalla de confirmación.
  const [ubicada, setUbicada] = useState<boolean | null>(null)
  /**
   * El punto de la obra, tomado por el técnico ANTES de crear (GPS o mapa).
   * Si lo puso, manda sobre la geocodificación de la dirección: es él quien
   * está parado en el sitio. Si no, la orden se geocodifica como siempre.
   */
  const [punto, setPunto] = useState<{ lat: number; lng: number } | null>(null)

  const { opciones: servicios, cargando: cargandoServicios } = useOpcionesServicios()
  const [creandoServicio, setCreandoServicio] = useState(false)

  const [cliente, setCliente] = useState<ClienteElegido | null>(null)
  /**
   * Si la dirección la sugirió el sistema o la escribió la persona.
   *
   * Importa para no pisar lo tecleado: quien ya escribió la dirección de la
   * obra no puede ver cómo se la reemplazan al corregir el nombre del
   * cliente en el buscador.
   */
  const [direccionSugerida, setDireccionSugerida] = useState(false)
  const [valores, setValores] = useState(VALORES_INICIALES)
  const [fechaSolicitud, setFechaSolicitud] = useState(hoyEnBogota)
  const [error, setError] = useState<string | null>(null)
  const [numeroCreado, setNumeroCreado] = useState<string | null>(null)
  /*
   * EL TÉCNICO NO VE LA CONFIRMACIÓN DE OFICINA.
   *
   * Decía «la orden ya suma en los tableros; cuando esté lista para cotizar,
   * márquela como pendiente de cotizar desde su detalle». Eso es cierto en la
   * matriz y no tiene sentido en la calle: él no tiene detalle ni menú, y lo
   * que va a hacer justo después —cotizar— ya se lo abre la aplicación sola.
   * Un cartel que manda hacer algo que no se puede hacer enseña a no leer los
   * carteles.
   *
   * Encadenado, la orden se crea y se ubica mientras el botón sigue girando;
   * cuando termina, se abre el cotizador. Este estado es el que lo mantiene
   * girando: la mutación ya acabó, pero el gesto no.
   */
  const [encadenando, setEncadenando] = useState(false)

  // Al abrir se limpia todo: reabrir el formulario con los datos de la orden
  // anterior es la forma más fácil de crear una orden duplicada sin notarlo.
  useEffect(() => {
    if (abierto) {
      setCliente(null)
      setValores(VALORES_INICIALES)
      setFechaSolicitud(hoyEnBogota())
      setError(null)
      setNumeroCreado(null)
      setEncadenando(false)
      setPunto(null)
    }
  }, [abierto])

  function cambiar(campo: keyof typeof VALORES_INICIALES, valor: string) {
    setValores((previos) => ({ ...previos, [campo]: valor }))
  }

  /**
   * LA DIRECCIÓN DE LA ORDEN ES LA OBRA, NO SIEMPRE LA DEL CLIENTE.
   *
   * Un contratista tiene su oficina registrada y trabaja en otra parte, así
   * que esto RELLENA COMO SUGERENCIA y la ayuda del campo dice de dónde salió.
   * Rellenar en silencio con la dirección de facturación es cómo se manda una
   * cuadrilla al sitio equivocado.
   *
   * Y no pisa lo que ya está escrito: si alguien tecleó la dirección de la
   * obra y después corrige el nombre del cliente, lo suyo manda.
   */
  async function traerDireccion(elegido: ClienteElegido | null) {
    if (!elegido) return
    if (valores.direccion.trim() !== '' && !direccionSugerida) return

    const { data } = await supabase
      .from('clientes')
      .select('direccion')
      .eq('wo_id_tercero', elegido.id_tercero)
      .maybeSingle()

    const direccion = (data as { direccion: string | null } | null)?.direccion?.trim()

    if (direccion) {
      cambiar('direccion', direccion)
      setDireccionSugerida(true)
    }
  }

  async function guardar(evento: FormEvent) {
    evento.preventDefault()
    setError(null)

    /* TODO ES OBLIGATORIO, y el aviso dice QUÉ falta.
       Un «complete los campos» genérico obliga a recorrer el formulario
       buscando el asterisco, y en un teléfono eso son dos pantallas de
       desplazamiento. */
    if (!cliente) {
      setError('Falta el cliente: escójalo en el buscador de arriba.')
      return
    }

    if (!valores.servicio_id) {
      setError('Falta el servicio. Si no está en la lista, se crea desde ahí mismo.')
      return
    }

    if (valores.direccion.trim().length < 4) {
      setError('Falta la dirección de la obra.')
      return
    }

    if (!fechaSolicitud) {
      setError('Falta la fecha de solicitud.')
      return
    }

    /* La descripción es lo único que viaja al que va a cotizar y al que va a
       ejecutar. Sin ella la orden existe y nadie sabe qué hay que hacer. */
    if (valores.descripcion.trim().length < 10) {
      setError('Falta la descripción del trabajo: qué hay que hacer, con el detalle suficiente para cotizarlo.')
      return
    }

    try {
      // El cliente vive en World Office; la orden necesita el de la matriz.
      // Se trae en este momento y no antes: buscar un cliente para mirarlo no
      // debería dejar rastro, solo usarlo de verdad.
      /*
       * SIN SEÑAL, EL CLIENTE SE ADOPTA DESPUÉS.
       *
       * Adoptarlo trae el tercero de World Office a la matriz, y eso es una
       * llamada al servidor. Sin cobertura reventaba aquí —«Failed to fetch»—
       * y la orden no se guardaba, aunque el resto ya estuviera listo. Se
       * apunta el tercero y se adopta al subir la orden, que es cuando de
       * verdad hace falta.
       */
      let clienteId: string | null = null

      if (navigator.onLine) {
        const { data, error: fallo } = await supabase.rpc('fn_adoptar_cliente', {
          p_id_tercero: cliente.id_tercero,
        })

        if (fallo) throw new Error(mensajeDeError(fallo))
        clienteId = data as string
      }

      // Ya no se maneja zona: la ubicación va por la dirección y su punto en el
      // mapa. La orden nace sin zona (la geocerca usa el radio por defecto).
      const { id, numero } = await crear.mutateAsync({
        cliente_id: clienteId as string,
        // Solo viaja cuando la orden se encola sin señal: con ella se adopta
        // el cliente al subirla.
        wo_id_tercero_pendiente: clienteId ? null : cliente.id_tercero,
        servicio_id: valores.servicio_id,
        zona_id: null,
        direccion: valores.direccion.trim(),
        descripcion: valores.descripcion.trim() || null,
        notas: valores.notas.trim() || null,
        fecha_solicitud: fechaSolicitud,
      })

      if (onCreada) setEncadenando(true)
      else setNumeroCreado(numero)

      // Y SE UBICA AHORA, no cuando alguien se acuerde. Este es el paso que
      // faltaba: las órdenes nacían sin punto y el mapa se quedaba en blanco
      // hasta que alguien entrara al mapa de calor —otra pantalla— a ubicarlas
      // en lote.
      //
      // Si el técnico tomó el punto en la obra (GPS o mapa), ESE manda: se
      // guarda como 'manual' —la mejor fuente que hay— y el geocodificador ya
      // no lo pisa. Si no lo puso, se geocodifica la dirección como siempre.
      // Si falla no pasa nada: la orden ya está creada y el mapa ofrecerá
      // ubicarla después.
      setUbicada(punto ? await guardarUbicacion(id, punto.lat, punto.lng) : await ubicarUna(id))

      // Y las fotos de la visita, que llevaban esperando a que la orden
      // existiera. Si alguna falla NO se deshace la orden: ya está creada y
      // es válida sin fotos, y se pueden añadir después desde su ficha.
      // EL PASO SIGUIENTE, cuando ya está todo guardado. Las fotos ya no van
      // aquí: se toman al final, después de cotizar, porque es entonces cuando
      // el técnico ha terminado de mirar la instalación y sabe qué hay que
      // retratar. Pedirlas antes obliga a adivinar qué va a hacer falta.
      onCreada?.({ id, numero, cliente: cliente.nombre, direccion: valores.direccion.trim() })
    } catch (fallo) {
      setEncadenando(false)
      setError(fallo instanceof Error ? fallo.message : 'No fue posible crear la orden.')
    }
  }

  const faltanMaestros = !cargandoServicios && servicios.length === 0

  // Confirmación con el número asignado. Es el dato que la persona necesita
  // apuntar o dictar, así que se muestra en grande antes de cerrar.
  if (numeroCreado) {
    return (
      <Modal
        abierto={abierto}
        titulo="Orden de servicio creada"
        onCerrar={onCerrar}
        pie={
          <>
            <Boton
              onClick={() => {
                setNumeroCreado(null)
                setUbicada(null)
                setValores(VALORES_INICIALES)
                setPunto(null)
              }}
            >
              Crear otra
            </Boton>
            <Boton variante="principal" onClick={onCerrar}>
              Listo
            </Boton>
          </>
        }
      >
        <Aviso tipo="exito" titulo={numeroCreado}>
          La orden quedó registrada y ya suma en los tableros, aunque todavía no tenga precio.
          Cuando esté lista para cotizar, márquela como <b>pendiente de cotizar</b> desde su
          detalle.
        </Aviso>

        {/* QUÉ PASÓ CON LA DIRECCIÓN. Se dice siempre, encontrada o no: que el
            punto del mapa aparezca en silencio está bien, pero que NO aparezca
            en silencio es cómo se llega a veintitrés órdenes sin ubicar sin que
            nadie se entere. */}
        {ubicada === false && (
          <Aviso tipo="alerta" titulo="Sin punto en el mapa">
            No se encontró «{valores.direccion.trim()}» en Cartago. La orden está bien creada,
            pero no saldrá en el mapa hasta que se ubique. Se puede hacer desde el propio mapa,
            o corrigiendo la dirección en el detalle.
          </Aviso>
        )}
      </Modal>
    )
  }

  return (
    <>
    <Modal
      abierto={abierto}
      titulo={onCreada ? 'Paso 1 de 2 · El trabajo' : 'Nueva orden de servicio'}
      subtitulo={
          onCreada
            ? 'Se registra el trabajo, y al guardar se pasa derecho a ponerle precio.'
            : 'Estación 1 del recorrido. Todavía no lleva precio.'
        }
      onCerrar={onCerrar}
      pie={
        <>
          {/* EL AVISO, JUNTO AL BOTÓN QUE NO FUNCIONÓ.
              Estaba arriba del formulario, y quien pulsa está abajo —el
              formulario tiene siete campos y en un teléfono son dos pantallas
              de desplazamiento—: el mensaje salía fuera de la vista y el botón
              parecía no hacer nada. La explicación de por qué algo no ocurrió
              tiene que estar donde la persona acaba de mirar. */}
          {error && <p className={estilos.avisoPie}>{error}</p>}

          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton
            variante="principal"
            onClick={(e) => void guardar(e)}
            trabajando={crear.isPending || encadenando}
            disabled={faltanMaestros}
          >
            {/* EL BOTÓN DICE LO QUE HACE EL GESTO ENTERO, no su primer paso.
                Encadenado, pulsar aquí registra el trabajo Y abre el precio
                acto seguido; llamarlo «Crear orden» describe la mitad, y a
                quien está en la calle le hace creer que todavía le falta
                buscar la orden en alguna lista para cotizarla. */}
            {onCreada ? 'Cotizar' : 'Crear orden'}
          </Boton>
        </>
      }
    >
      <form className={estilos.rejilla} onSubmit={(e) => void guardar(e)}>
        {error && (
          <div className={estilos.error}>
            <Aviso tipo="error">{error}</Aviso>
          </div>
        )}

        {faltanMaestros && (
          <div className={estilos.nota}>
            <Aviso tipo="alerta" titulo="Faltan los servicios">
              Todavía no hay ninguno. Créelo con «Nuevo servicio», aquí abajo.
            </Aviso>
          </div>
        )}

        <div className={estilos.completo}>
          <BuscadorCliente
            elegido={cliente}
            onElegir={(c) => {
              setCliente(c)
              void traerDireccion(c)
            }}
            obligatorio
            ayuda="Escriba al menos dos letras. Si no existe, se puede crear sin salir de aquí."
          />
        </div>

        {/* El catálogo de servicios no tiene pantalla propia: se administra
            aquí, que es donde uno se entera de que falta uno. */}
        <div>
          <Selector
            etiqueta="Servicio"
            opciones={servicios}
            obligatorio
            value={valores.servicio_id}
            onChange={(e) => cambiar('servicio_id', e.target.value)}
            textoVacio={cargandoServicios ? 'Cargando servicios…' : 'Seleccione el servicio…'}
          />

          {/* SOLO GERENCIA. Justo debajo del selector se confundía con «crear
              cliente»: el 30/09 apareció un servicio llamado «Mario aguirre».
              La base también lo impide (0226). */}
          {puedeCrearServicio && (
            <button
              type="button"
              className={estilos.nuevoServicio}
              onClick={() => setCreandoServicio(true)}
            >
              ¿No está en la lista? Crear servicio
            </button>
          )}
        </div>

        {/* La dirección: de ella sale el punto en el mapa. */}
        <div className={estilos.completo}>
          <Campo
            etiqueta="Dirección"
            obligatorio
            value={valores.direccion}
            onChange={(e) => {
              setDireccionSugerida(false)
              cambiar('direccion', e.target.value)
            }}
            placeholder="Carrera 8 # 14-32, barrio Santa Ana"
            ayuda={
              direccionSugerida
                ? 'Es la dirección registrada del cliente. Cámbiela si el trabajo es en otro sitio.'
                : 'Tal como la dicta el cliente. El barrio o la vereda al final, después de la coma.'
            }
          />
        </div>

        {/* El punto en el mapa. El técnico está en la obra: toma el GPS y la
            orden sale ubicada desde ya, sin depender de que Google acierte con
            la dirección ni de que la oficina la ubique después. */}
        <div className={estilos.completo}>
          <SelectorUbicacion
            punto={punto}
            onPunto={setPunto}
            direccion={valores.direccion}
            // Sin municipio Google no sabe dónde buscar (OS-2026-0104).
            ciudad={cliente?.ciudad ?? null}
            departamento={cliente?.departamento ?? null}
          />
        </div>

        <Campo
          etiqueta="Fecha de solicitud"
          type="date"
          obligatorio
          value={fechaSolicitud}
          onChange={(e) => setFechaSolicitud(e.target.value)}
          ayuda="Primera de las tres fechas que usa la programación."
        />

        <div className={estilos.completo}>
          <AreaTexto
            etiqueta="Descripción del trabajo"
            obligatorio
            value={valores.descripcion}
            onChange={(e) => cambiar('descripcion', e.target.value)}
            ayuda="Qué hay que hacer, con lo que haga falta para cotizarlo sin volver a preguntar."
          />
        </div>

        <div className={estilos.completo}>
          <AreaTexto
            etiqueta="Notas internas"
            value={valores.notas}
            onChange={(e) => cambiar('notas', e.target.value)}
          />
        </div>

        {/* El envío del formulario con Intro. Solo lo ven los lectores de
            pantalla, y tiene que decir lo mismo que el botón visible. */}
        <button type="submit" className="solo-lectores" tabIndex={-1}>
          {onCreada ? 'Cotizar' : 'Crear orden'}
        </button>
      </form>
    </Modal>

      <NuevoServicio
        abierto={creandoServicio}
        onCerrar={() => setCreandoServicio(false)}
        onCreado={(id) => cambiar('servicio_id', id)}
      />
    </>
  )
}
